import React, { useState, useEffect } from 'react';
import DataGrid from '../../../Component/DataGrid/DataGrid';
import { TableColumn } from 'react-data-table-component';
import FxAllocationService from '../../../Services/FxAllocationService';
import { loadFromLocalStorage } from '../../../Common/Validation/LocalStorage';
import { getLast30DaysRange, getRequestDataForReportsWithDateAndPage, Texts } from '../../../Component/Reusables/Utility';
import moment from 'moment';
import { Box } from '@mui/material';
import ReusableDrawer from '../../../Component/RightDrawer/ReusableDrawer';
import { ISelectedDateRangeString } from '../../../Component/DataGrid/Interface';
import { ShowAlert } from '../../../Component/ShowAlert';
import { fetchRecipientCountryList } from '../../../Component/Reusables/Recipient';

interface AllocationData {
    transactionPaymentId: string;
    poolId: string;
    allocatedUsdAmount: string;
    allocationStatusId: number;
    createdUtc: string;
    totalCostUsd: string;
    grossMarginUsd: string;
    grossMarginPercentage: number;
    isDrawerOpen: boolean;
    appliedFiltersCount: number;
    countryId: string;
    selectedDateRange: ISelectedDateRangeString;
    destinationAmount?: string;
    toCurrencySymbol?: string;
}

const FxAllocationList: React.FC = () => {
    let monthRange = getLast30DaysRange();
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [currentRowsPerPage, setCurrentRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [allocations, setAllocations] = useState<AllocationData[]>([]);
    const [accessToken, setAccessToken] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);
    const [countryId, setCountryId] = useState('');
    const [csvData, setCsvData] = useState<AllocationData[]>([]);
    const [selectedDateRange, setSelectedDateRange] = useState<ISelectedDateRangeString>({
        startDate: monthRange.startDate,
        endDate: monthRange.endDate,
    });

    const tableColumns: TableColumn<AllocationData>[] = [
        {
            name: 'Transaction ID',
            selector: row => row.transactionPaymentId,
            sortable: true,
            id: "transaction_id",
            wrap: true
        },
        {
            name: 'Pool ID',
            selector: row => row.poolId,
            sortable: true,
            id: "pool_id",
            wrap: true
        },
        {
            name: 'Transaction Date',
            selector: row => moment(row.createdUtc).format(Texts.formatDateTimeDisplay),
            sortable: true,
            id: "created_date",
        },
        {
            name: 'Total Cost (USD)',
            sortable: true,
            id: "totalCostUsd",
            right: true,
            selector: row => `$ ${row.totalCostUsd}`,
            minWidth: '120px'
        },
        {
            name: 'Total Cost',
            sortable: true,
            id: "destinationAmount",
            right: true,
            selector: row => `${row.toCurrencySymbol} ${row.destinationAmount}`,
            minWidth: '120px'
        },
        {
            name: 'Gross Margin (USD)',
            sortable: true,
            id: "grossMarginUsd",
            right: true,
            selector: row => `$ ${row.grossMarginUsd}`,
            minWidth: '130px'
        },
        {
            name: 'Gross Margin %',
            sortable: true,
            id: "grossMarginPercentage",
            right: true,
            selector: row => `${row.grossMarginPercentage}%`,
            minWidth: '120px'
        }
    ];

    useEffect(() => {
        const initialize = async () => {
            const token = await loadFromLocalStorage('accessToken');
            if (token) {
                setAccessToken(token.toString());
            }
        };
        initialize();
    }, []);

    useEffect(() => {
        if (accessToken) {
            fetchReportData();
        }
    }, [accessToken, currentPage, currentRowsPerPage, searchText, selectedDateRange, countryId]);

    // const getAllocationList = async () => {
    //     try {
    //         const pageIndex = 1;
    //         const pagination = {
    //             pageNumber: pageIndex,
    //             recordPerPage: currentRowsPerPage,
    //             ...(searchText && { searchText })
    //         };

    //         const response = await FxAllocationService.getAllocationList(accessToken, pagination);

    //         if (response.ok) {
    //             const responseData = await response.json();
    //             setAllocations(Array.isArray(responseData.data) ? responseData.data : []);
    //             setTotalRows(responseData.totalRecords || 0);
    //         }
    //     } catch (error) {
    //         console.error('Failed to fetch allocations:', error);
    //     }
    // };

    const fetchReportData = async () => {
        try {
            const requestData = {
                accessToken: accessToken,
                currentRowsPerPage: currentRowsPerPage,
                currentPage: currentPage,
                totalRows: totalRows,
                searchText: searchText,
                selectedDateRange: selectedDateRange,
                countryId: countryId
            }
            const response = await FxAllocationService.getAllocationList(accessToken, getRequestDataForReportsWithDateAndPage(requestData));

            if (response.ok) {
                const responseData = await response.json();
                setAllocations(Array.isArray(responseData.data) ? responseData.data : []);
                setTotalRows(responseData.totalRecords || 0);
            }

            // Fetch CSV data
            const csvResult = await FxAllocationService.getAllocationList(accessToken, getRequestDataForReportsWithDateAndPage(requestData, true), true);
            if (csvResult.ok) {
                const csvResponseData = await csvResult.json();
                setCsvData(Array.isArray(csvResponseData.data) ? csvResponseData.data : []);
            }
        } catch (error) {
            ShowAlert("Error", "Failed to load FX Placeholder rate data", "error");
        }
    };

    const handleSearch = (searchText: string) => {
        setSearchText(searchText);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (rowsPerPage: number, page: number) => {
        setCurrentRowsPerPage(rowsPerPage);
        setCurrentPage(page);
    };

    const handleDrawerApply = (filters: { selectedId: string; startDate: string | null; endDate: string | null }) => {
        const { selectedId, startDate, endDate } = filters;
        setSelectedDateRange({
            startDate: startDate || null,
            endDate: endDate || null,
        });
        setCurrentPage(1);
        setCountryId(selectedId);
    };

    const fetchDropdownOptions = () => {
        return fetchRecipientCountryList(accessToken);
    };

    return (
        <Box className='body-sub-contianer table-container'>
            <DataGrid
                title="FX Transaction Costs and Margins"
                search="allocations"
                onSearch={handleSearch}
                defaultSearch={searchText}
                columns={tableColumns}
                data={allocations}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
                pagination
                paginationTotalRows={totalRows}
                exportCsv={true}
                isDrawerRequired={true}
                isDrawerOpen={() => setIsDrawerOpen(true)}
                appliedFiltersCount={appliedFiltersCount}
                csvData={csvData}
                csvFileName='FX-Allocations'
                selectedCountry={countryId}
            />

            <ReusableDrawer
                open={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onApply={handleDrawerApply}
                fetchDropdownOptions={fetchDropdownOptions}
                defaultValue={countryId}
                onFiltersCountChange={(count) => setAppliedFiltersCount(count)}
                defaultStartDate={selectedDateRange.startDate}
                defaultEndDate={selectedDateRange.endDate}
                showDateRange={true}
                showTxnStatusFilter={false}
            />
        </Box>
    );
};

export default FxAllocationList;
